import { inject } from '@angular/core';

import { Store } from '@ngrx/store';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { I18nService } from '@core/i18n-legacy';
import {
  flexiblePositionStrategyFactory,
  globalPositionStrategyFactory,
  Step,
  WalkthroughDialogComponent,
  WalkthroughTooltipComponent,
} from '@core/walkthrough-legacy';
import { walkthroughAssetsResolver } from '@core/walkthrough-util';

import { AutomationsWalkthroughStepBuilder } from './automations-walkthrough-step-builder';
import { AutomationsWalkthroughStepId } from '../automations-walkthruogh.types';

export class OptimizeAutomationsWalkthroughStepBuilder
  implements AutomationsWalkthroughStepBuilder
{
  private readonly i18n = inject(I18nService);
  private readonly store = inject(Store);
  private readonly hasManageAA = this.store.selectSignal(
    AbilitiesSelectors.hasOne(Ability.ManageAutomatedActions),
  );

  // eslint-disable-next-line max-lines-per-function
  public build(): Step[] {
    return [
      {
        id: AutomationsWalkthroughStepId.CommonStep,
        position: 1,
        disableOnboardingEndpoint: true,
        component: {
          definition: WalkthroughDialogComponent,
          inputs: {
            heading: this.i18n.translate(
              'app.workflow_automations.feature_discovery.walkthrough.optimize.feature_discovery_dialog.title',
            ),
            description: this.i18n.translate(
              'app.workflow_automations.feature_discovery.walkthrough.optimize.feature_discovery_dialog.description',
            ),
            ctaText: this.hasManageAA()
              ? this.i18n.translate(
                  'app.workflow_automations.feature_discovery.walkthrough.cta_text',
                )
              : this.i18n.translate('app.common.got_it'),
            imageUrl: walkthroughAssetsResolver('workflow_automations.svg'),
            showStepCounter: false,
            showSkipButton: this.hasManageAA(),
            showCloseButton: true,
            displayCtaOnly: !this.hasManageAA(),
          },
        },
        renderOptions: {
          strategy: globalPositionStrategyFactory,
        },
      },
      {
        id: AutomationsWalkthroughStepId.OptimizeStep2,
        position: 2,
        disableOnboardingEndpoint: true,
        component: {
          definition: WalkthroughTooltipComponent,
          inputs: {
            arrowConfig: ['top', 'right'],
            heading: this.i18n.translate(
              'app.workflow_automations.feature_discovery.walkthrough.optimize.feature_discovery_dialog.step_2.title',
            ),
            description: this.i18n.translate(
              'app.workflow_automations.feature_discovery.walkthrough.optimize.feature_discovery_dialog.step_2.content',
            ),
            showStepCounter: true,
            ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
            ctaIcon: 'chevron-right',
          },
        },
        renderOptions: {
          strategy: flexiblePositionStrategyFactory,
          useWalkthroughBackdropClass: true,
          positions: [
            {
              originX: 'start',
              originY: 'center',
              overlayX: 'end',
              overlayY: 'center',
              offsetX: -18,
              offsetY: 36,
            },
          ],
        },
      },
      {
        id: AutomationsWalkthroughStepId.OptimizeStep3,
        position: 3,
        disableOnboardingEndpoint: true,
        component: {
          definition: WalkthroughTooltipComponent,
          inputs: {
            arrowConfig: ['left', 'top'],
            heading: this.i18n.translate(
              'app.workflow_automations.feature_discovery.walkthrough.optimize.feature_discovery_dialog.step_3.title',
            ),
            description: this.i18n.translate(
              'app.workflow_automations.feature_discovery.walkthrough.optimize.feature_discovery_dialog.step_3.content',
            ),
            showStepCounter: true,
            ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
            ctaIcon: 'chevron-right',
          },
        },
        renderOptions: {
          strategy: flexiblePositionStrategyFactory,
          useWalkthroughBackdropClass: true,
          positions: [
            {
              originX: 'start',
              originY: 'bottom',
              overlayX: 'start',
              overlayY: 'top',
              offsetX: -15,
            },
          ],
        },
      },
      {
        id: AutomationsWalkthroughStepId.OptimizeStep4,
        position: 4,
        disableOnboardingEndpoint: true,
        component: {
          definition: WalkthroughTooltipComponent,
          inputs: {
            arrowConfig: ['left', 'bottom'],
            heading: this.i18n.translate(
              'app.workflow_automations.feature_discovery.walkthrough.optimize.feature_discovery_dialog.step_4.title',
            ),
            description: this.i18n.translate(
              'app.workflow_automations.feature_discovery.walkthrough.optimize.feature_discovery_dialog.step_4.content',
            ),
            showStepCounter: true,
            ctaText: this.i18n.translate('app.common.got_it'),
          },
        },
        renderOptions: {
          strategy: flexiblePositionStrategyFactory,
          useWalkthroughBackdropClass: true,
          positions: [
            {
              originX: 'start',
              originY: 'top',
              overlayX: 'start',
              overlayY: 'bottom',
              offsetY: -40,
            },
          ],
        },
      },
    ];
  }
}
